import {
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
} from '@chakra-ui/react';
import { countriesInfo, type Country } from '@/models/country';
import { type GroupStandingRow, mergeStandingsWithRoster } from '@/models/groupStandings';
import { Flag } from './Flag';
import { singleLineEllipsisTextProps } from './singleLineEllipsis';
import { TeamDetailTrigger } from './teamDetail/TeamDetailTrigger';

export type GroupStandingsTableVariant = 'teams-page' | 'modal';

/** Minimum table width so stat columns scroll instead of compressing (page body is overflow-x hidden). */
const TABLE_MIN_W: Record<GroupStandingsTableVariant, string> = {
  'teams-page': '22rem',
  modal: '20rem',
};

export function GroupStandingsTable(props: {
  teams: Country[];
  standings?: GroupStandingRow[];
  variant?: GroupStandingsTableVariant;
  teamClickSource?: 'teams-page' | 'fixtures';
}) {
  const variant = props.variant ?? 'modal';
  const standings = mergeStandingsWithRoster(props.standings ?? [], props.teams);
  const source = props.teamClickSource ?? 'teams-page';
  const isTeamsPage = variant === 'teams-page';

  const teamFlagW = useBreakpointValue(
    isTeamsPage ? { base: 6, lg: 8 } : { base: 6, md: 7 },
  ) ?? (isTeamsPage ? 8 : 7);
  const teamFontSize = useBreakpointValue(isTeamsPage ? { base: 'xs', lg: 'sm' } : { base: 'xs', md: 'sm' }) ?? 'sm';
  const teamFontWeight = isTeamsPage ? 'semibold' : 'medium';
  const statFontSize = 'xs';
  const headerFontSize = 'xs';
  const statPx = 0.5;
  const teamPx = 2;
  const rowPy = useBreakpointValue(isTeamsPage ? { base: 2, lg: 2.5 } : { base: 2, md: 2 }) ?? 2;
  const teamGap = useBreakpointValue(isTeamsPage ? { base: 2, lg: 2.5 } : { base: 2, md: 2 }) ?? 2;
  const teamColWidth = variant === 'teams-page' ? '46%' : '42%';

  return (
    <TableContainer
      w='full'
      minW={0}
      overflowX='auto'
      mx={-1}
      px={1}
      pb={1}
      sx={{ WebkitOverflowScrolling: 'touch' }}
    >
      <Table
        size='sm'
        variant='simple'
        minW={TABLE_MIN_W[variant]}
        sx={{
          tableLayout: 'fixed',
          'th, td': { lineHeight: 'short' },
          'th:first-of-type, td:first-of-type': {
            width: teamColWidth,
            maxWidth: 0,
          },
          'th:not(:first-of-type), td:not(:first-of-type)': {
            whiteSpace: 'nowrap',
          },
        }}
      >
        <Thead>
          <Tr>
            <Th px={teamPx} fontSize={headerFontSize} textTransform='none' letterSpacing='normal'>
              Team
            </Th>
            <Th px={statPx} isNumeric fontSize={headerFontSize} textTransform='none'>
              Pts
            </Th>
            <Th px={statPx} isNumeric fontSize={headerFontSize} textTransform='none'>
              P
            </Th>
            <Th px={statPx} isNumeric fontSize={headerFontSize} textTransform='none'>
              GD
            </Th>
            <Th px={statPx} isNumeric fontSize={headerFontSize} textTransform='none'>
              W
            </Th>
            <Th px={statPx} isNumeric fontSize={headerFontSize} textTransform='none'>
              D
            </Th>
            <Th px={statPx} isNumeric fontSize={headerFontSize} textTransform='none'>
              L
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {standings.map((row, index) => (
            <Tr key={row.country}>
              <Td px={teamPx} py={rowPy} overflow='hidden'>
                <TeamDetailTrigger country={row.country} source={source} variant='row'>
                  <Flex align='center' gap={teamGap} minW={0} w='full' overflow='hidden'>
                    <Text
                      fontSize='2xs'
                      fontWeight='bold'
                      color='gray.500'
                      flexShrink={0}
                      w='1.25rem'
                      textAlign='center'
                    >
                      {index + 1}
                    </Text>
                    <Flag country={row.country} w={teamFlagW} borderRadius='3px' svg flexShrink={0} />
                    <Text
                      {...singleLineEllipsisTextProps}
                      flex={1}
                      fontSize={teamFontSize}
                      fontWeight={teamFontWeight}
                      lineHeight='short'
                      textAlign='left'
                    >
                      {countriesInfo[row.country].name}
                    </Text>
                  </Flex>
                </TeamDetailTrigger>
              </Td>
              <Td px={statPx} py={rowPy} isNumeric fontWeight='semibold' fontSize={statFontSize}>
                {row.pts}
              </Td>
              <Td px={statPx} py={rowPy} isNumeric fontSize={statFontSize}>
                {row.mp}
              </Td>
              <Td px={statPx} py={rowPy} isNumeric fontSize={statFontSize} whiteSpace='nowrap'>
                {row.gd > 0 ? `+${row.gd}` : row.gd}
              </Td>
              <Td px={statPx} py={rowPy} isNumeric fontSize={statFontSize}>
                {row.w}
              </Td>
              <Td px={statPx} py={rowPy} isNumeric fontSize={statFontSize}>
                {row.d}
              </Td>
              <Td px={statPx} py={rowPy} isNumeric fontSize={statFontSize}>
                {row.l}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}
